/**
 * RBAC-8.1 · FR-27, FR-28. A role's history: every version as the change from the one before it, in
 * the save flow's words, and a way back to any of them.
 *
 * Renders `historyEntries()` and nothing else. Restore does not happen here: it hands the version to
 * the Review dialog (`ReviewChangesDialog` with `restore`), so a restore is reviewed, counted and
 * confirmed exactly like an ordinary save.
 *
 * Restore is held back while the role has unsaved changes: the restore replaces the role's saved
 * permissions, and an admin with a half-made draft should not lose it to a click in another dialog.
 */
import { useQuery } from '@tanstack/react-query';
import { History, Loader2, Minus, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { DiffLine } from '@/lib/rolePermissionDiff';
import { baselineSummary, historyEntries, type HistoryEntry } from '@/lib/roleHistory';
import { fetchRoleHistory } from '@/services/roleHistoryService';

export const roleHistoryQueryKey = (roleId: number) => ['rbac', 'role-history', roleId] as const;

function Lines({ tone, lines }: { tone: 'gain' | 'loss'; lines: DiffLine[] }) {
  if (lines.length === 0) return null;
  const gain = tone === 'gain';
  const Icon = gain ? Plus : Minus;
  return (
    <ul className="space-y-1.5">
      {lines.map((l) => (
        <li
          key={l.key}
          className={
            gain
              ? 'rounded-r-md border-l-4 border-emerald-500 bg-emerald-50 px-3 py-1.5 dark:bg-emerald-950/40'
              : 'rounded-r-md border-l-4 border-red-500 bg-red-50 px-3 py-1.5 dark:bg-red-950/40'
          }
        >
          <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Icon className={gain ? 'h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-300' : 'h-3.5 w-3.5 shrink-0 text-red-700 dark:text-red-300'} aria-label={gain ? 'Gained' : 'Lost'} />
            {l.title}
          </p>
          <p className="text-sm text-muted-foreground">{l.sentence}</p>
        </li>
      ))}
    </ul>
  );
}

function Entry({
  entry,
  restoreBlocked,
  onRestore,
}: {
  entry: HistoryEntry;
  restoreBlocked: boolean;
  onRestore: () => void;
}) {
  return (
    <li className="space-y-2 border-b border-border py-4 last:border-b-0" data-version-id={entry.version.id}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{entry.heading}</p>
          <p className="text-xs text-muted-foreground">
            {entry.when}
            {entry.isCurrent && ' · Current'}
          </p>
        </div>
        {entry.restorable && (
          <Button variant="outline" size="sm" onClick={onRestore} disabled={restoreBlocked}>
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Restore this version
          </Button>
        )}
      </div>
      {entry.showsDiff ? (
        <div className="space-y-1.5">
          <Lines tone="gain" lines={entry.gained} />
          <Lines tone="loss" lines={entry.lost} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{baselineSummary(entry.version)}</p>
      )}
    </li>
  );
}

export function RoleHistoryDialog({
  open,
  roleId,
  hasUnsavedChanges,
  onOpenChange,
  onRestore,
}: {
  open: boolean;
  roleId: number;
  hasUnsavedChanges: boolean;
  onOpenChange: (open: boolean) => void;
  /** The version to review for restore, and how to name it. */
  onRestore: (version: { versionId: number; when: string }) => void;
}) {
  const history = useQuery({
    queryKey: roleHistoryQueryKey(roleId),
    queryFn: () => fetchRoleHistory(roleId),
    enabled: open,
    staleTime: 0,
  });
  const entries = history.data ? historyEntries(history.data) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" aria-hidden />
            History
          </DialogTitle>
          <DialogDescription>
            {history.data
              ? `${history.data.roleName}. Every change to what this role can do, newest first. Restoring a version is reviewed and confirmed like any other change.`
              : 'Every change to what this role can do, newest first.'}
          </DialogDescription>
        </DialogHeader>

        {history.isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {history.isError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {history.error instanceof Error ? history.error.message : 'The history could not be loaded.'}
          </p>
        )}

        {hasUnsavedChanges && entries && entries.some((e) => e.restorable) && (
          <p className="rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground">
            This role has unsaved changes. Save or discard them before restoring a version.
          </p>
        )}

        {entries && entries.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing has been changed on this role yet.</p>
        )}

        {entries && entries.length > 0 && (
          <ol className="-my-2">
            {entries.map((e) => (
              <Entry
                key={e.version.id}
                entry={e}
                restoreBlocked={hasUnsavedChanges}
                onRestore={() => onRestore({ versionId: e.version.id, when: e.when })}
              />
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
